import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { accountsApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Spinner, Alert, BackLink, EmptyState } from '@rankhelm/ui';
import { workspaceTitles } from '../lib/workspace';
import type { Account, User, Workspace } from '@rankhelm/shared';

export function AccountDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const { user, impersonate } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState<Account | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;

    async function load() {
      try {
        const [acc, usr, wss] = await Promise.all([
          accountsApi.get(accountId!),
          accountsApi.listUsers(accountId!),
          accountsApi.listWorkspaces(accountId!),
        ]);
        if (cancelled) return;
        setAccount(acc.data);
        setUsers(usr.data || []);
        setWorkspaces(wss.data || []);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load account');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  async function handleImpersonate(target: User) {
    if (!isSuperAdmin) return;
    if (target.role === 'super_admin') {
      setError('Cannot impersonate another super admin.');
      return;
    }
    setError('');
    setImpersonatingId(target.id);
    try {
      await impersonate(target.id);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start impersonation');
      setImpersonatingId(null);
    }
  }

  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">You don't have permission to view account details.</p>
      </div>
    );
  }

  if (loading) return <Spinner message="Loading account..." fullPage />;

  if (!account) {
    return (
      <div>
        <Alert variant="error">{error || 'Account not found'}</Alert>
      </div>
    );
  }

  return (
    <div>
      <BackLink to="/accounts" label="Back to Accounts" />
      <div className="mb-8">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-2xl font-bold text-white">{account.name}</h1>
          {account.display_id && (
            <span className="px-2 py-0.5 bg-navy-700 text-slate-300 text-xs font-medium rounded font-mono">
              {account.display_id}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500 mt-1">
          {account.slug} · {account.type} · {account.plan_tier}
        </p>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-navy-700">
            <h2 className="text-lg font-semibold text-white">Users</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {users.length} {users.length === 1 ? 'user' : 'users'} in this account
            </p>
          </div>
          {users.length === 0 ? (
            <div className="p-6">
              <EmptyState message="No users in this account yet." />
            </div>
          ) : (
            <ul className="divide-y divide-navy-700/50">
              {users.map((u) => (
                <li key={u.id} className="px-5 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-medium truncate">{u.name || u.email}</p>
                    <p className="text-xs text-slate-400 truncate">{u.email}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{u.role?.replace('_', ' ')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleImpersonate(u)}
                    disabled={u.role === 'super_admin' || impersonatingId === u.id}
                    className="px-3 py-1.5 text-xs font-medium bg-helm-500 hover:bg-helm-400 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={u.role === 'super_admin' ? 'Cannot impersonate another super admin' : 'Log in as this user'}
                  >
                    {impersonatingId === u.id ? 'Starting…' : 'Log in as this user'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Workspaces */}
        <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-navy-700">
            <h2 className="text-lg font-semibold text-white">Workspaces</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {workspaces.length} {workspaces.length === 1 ? 'workspace' : 'workspaces'} in this account
            </p>
          </div>
          {workspaces.length === 0 ? (
            <div className="p-6">
              <EmptyState message="No workspaces yet. Impersonate an account admin to create one." />
            </div>
          ) : (
            <ul className="divide-y divide-navy-700/50">
              {workspaces.map((ws) => {
                const titles = workspaceTitles(ws);
                return (
                <li key={ws.id} className="px-5 py-4">
                  <Link
                    to={`/workspaces/${ws.id}`}
                    className="block hover:bg-navy-800/50 rounded-md -m-2 p-2 transition-colors"
                  >
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <p className="text-sm text-white font-medium truncate">{titles.primary}</p>
                      {ws.display_id && (
                        <span className="px-1.5 py-0.5 bg-navy-700 text-slate-300 text-[10px] font-medium rounded font-mono">
                          {ws.display_id}
                        </span>
                      )}
                    </div>
                    {titles.secondary && (
                      <p className="text-xs text-slate-400 truncate">{titles.secondary}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-0.5">{ws.slug}</p>
                  </Link>
                </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
